/**
 * Minimal WebAuthn JSON helpers for CSS passkey ceremonies.
 * CSS API speaks snake_case; browser APIs want camelCase ArrayBuffers.
 */

function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer);
  let str = "";
  bytes.forEach((b) => {
    str += String.fromCharCode(b);
  });
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function base64urlToBuffer(base64url) {
  const padded = base64url.replace(/-/g, "+").replace(/_/g, "/");
  const pad = padded.length % 4 === 0 ? "" : "=".repeat(4 - (padded.length % 4));
  const str = atob(padded + pad);
  const bytes = new Uint8Array(str.length);
  for (let i = 0; i < str.length; i += 1) {
    bytes[i] = str.charCodeAt(i);
  }
  return bytes.buffer;
}

function mapCredentialDescriptor(descriptor) {
  return {
    type: descriptor.type,
    id: base64urlToBuffer(descriptor.id),
    transports: descriptor.transports,
  };
}

/**
 * Server options (snake_case) → PublicKeyCredentialCreation/RequestOptions for the browser.
 */
export function publicKeyOptionsFromServer(payload) {
  const {
    ceremony_id: _ceremonyId,
    challenge,
    user,
    exclude_credentials: excludeCredentials,
    allow_credentials: allowCredentials,
    timeout,
    rp,
    pub_key_cred_params: pubKeyCredParams,
    authenticator_selection: authenticatorSelection,
    attestation,
    extensions,
    user_verification: userVerification,
    rp_id: rpId,
  } = payload;

  const options = {
    challenge: base64urlToBuffer(challenge),
  };

  if (timeout != null) options.timeout = timeout;
  if (rp) options.rp = rp;
  if (rpId) options.rpId = rpId;
  if (pubKeyCredParams) options.pubKeyCredParams = pubKeyCredParams;
  if (authenticatorSelection) {
    options.authenticatorSelection = {
      authenticatorAttachment: authenticatorSelection.authenticator_attachment,
      residentKey: authenticatorSelection.resident_key,
      requireResidentKey: authenticatorSelection.require_resident_key,
      userVerification: authenticatorSelection.user_verification,
    };
    Object.keys(options.authenticatorSelection).forEach((key) => {
      if (options.authenticatorSelection[key] === undefined) {
        delete options.authenticatorSelection[key];
      }
    });
  }
  if (attestation) options.attestation = attestation;
  if (extensions) options.extensions = extensions;
  if (userVerification) options.userVerification = userVerification;

  if (user) {
    options.user = {
      id: typeof user.id === "string" ? base64urlToBuffer(user.id) : user.id,
      name: user.name,
      displayName: user.display_name,
    };
  }

  if (excludeCredentials) {
    options.excludeCredentials = excludeCredentials.map(mapCredentialDescriptor);
  }

  if (allowCredentials) {
    options.allowCredentials = allowCredentials.map(mapCredentialDescriptor);
  }

  return options;
}

export function credentialToServerJson(credential) {
  const response = credential.response;
  const clientDataJSON = bufferToBase64url(response.clientDataJSON);

  if (response.attestationObject) {
    return {
      id: credential.id,
      raw_id: bufferToBase64url(credential.rawId),
      type: credential.type,
      authenticator_attachment: credential.authenticatorAttachment,
      client_extension_results: credential.getClientExtensionResults
        ? credential.getClientExtensionResults()
        : {},
      response: {
        client_data_json: clientDataJSON,
        attestation_object: bufferToBase64url(response.attestationObject),
        transports: response.getTransports ? response.getTransports() : [],
      },
    };
  }

  return {
    id: credential.id,
    raw_id: bufferToBase64url(credential.rawId),
    type: credential.type,
    authenticator_attachment: credential.authenticatorAttachment,
    client_extension_results: credential.getClientExtensionResults
      ? credential.getClientExtensionResults()
      : {},
    response: {
      client_data_json: clientDataJSON,
      authenticator_data: bufferToBase64url(response.authenticatorData),
      signature: bufferToBase64url(response.signature),
      user_handle: response.userHandle
        ? bufferToBase64url(response.userHandle)
        : null,
    },
  };
}

export function isWebAuthnAvailable() {
  return (
    typeof window !== "undefined" &&
    window.PublicKeyCredential &&
    typeof navigator.credentials?.create === "function" &&
    typeof navigator.credentials?.get === "function"
  );
}

export async function isConditionalMediationAvailable() {
  if (!isWebAuthnAvailable()) return false;
  if (typeof PublicKeyCredential.isConditionalMediationAvailable !== "function") {
    return false;
  }
  try {
    return await PublicKeyCredential.isConditionalMediationAvailable();
  } catch (_err) {
    return false;
  }
}

export async function createPasskeyCredential(serverOptions) {
  const publicKey = publicKeyOptionsFromServer(serverOptions);
  const credential = await navigator.credentials.create({ publicKey });
  if (!credential) {
    throw new Error("Passkey creation was cancelled or failed.");
  }
  return credentialToServerJson(credential);
}

export async function getPasskeyCredential(serverOptions, { mediation, signal } = {}) {
  const publicKey = publicKeyOptionsFromServer(serverOptions);
  const request = { publicKey };
  if (mediation) {
    request.mediation = mediation;
  }
  if (signal) {
    request.signal = signal;
  }
  const credential = await navigator.credentials.get(request);
  if (!credential) {
    throw new Error("Passkey sign-in was cancelled or failed.");
  }
  return credentialToServerJson(credential);
}
